import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface InfoSidebarState {
  isOpen: boolean;
  activeTab: 'info' | 'gene' | 'mapping' | null;
  width: number;
  position: 'left' | 'right';
  
  setIsOpen: (open: boolean) => void;
  setActiveTab: (tab: 'info' | 'gene' | 'mapping' | null) => void;
  setWidth: (width: number) => void;
  setPosition: (position: 'left' | 'right') => void;
  
  toggle: () => void;
  open: (tab?: 'info' | 'gene' | 'mapping') => void;
  close: () => void;
  
  openWithInfo: (item: any) => void;
  openWithGene: (symbol: string, id: number | null, name: string) => void;
  openWithMapping: () => void;
}

const getDefaultSidebarState = () => ({
  isOpen: false,
  activeTab: null as 'info' | 'gene' | 'mapping' | null,
  width: 400,
  position: 'right' as const,
});

export const useInfoSidebarStore = create<InfoSidebarState>()(
  devtools(
    (set) => ({
      ...getDefaultSidebarState(),

      setIsOpen: (isOpen) => set({ isOpen }),
      setActiveTab: (activeTab) => set({ activeTab }),
      setWidth: (width) => set({ width }),
      setPosition: (position) => set({ position }),

      toggle: () => 
        set((state) => ({ 
          isOpen: !state.isOpen,
          activeTab: !state.isOpen ? state.activeTab || 'info' : null
        })),

      open: (tab = 'info') => 
        set({ 
          isOpen: true, 
          activeTab: tab 
        }),

      close: () => 
        set({ 
          isOpen: false, 
          activeTab: null 
        }),

      openWithInfo: (item: any) => {
        set({ 
          isOpen: true, 
          activeTab: 'info' 
        });
      },

      openWithGene: (symbol: string, id: number | null, name: string) => {
        set({ 
          isOpen: true, 
          activeTab: 'gene' 
        });
      },

      openWithMapping: () => {
        set({ 
          isOpen: true, 
          activeTab: 'mapping' 
        });
      },
    }),
    {
      name: 'info-sidebar-store',
    },
  ),
);

export const useInfoSidebarSelectors = () => {
  const sidebar = useInfoSidebarStore();

  return {
    isInfoTabActive: sidebar.activeTab === 'info',
    isGeneTabActive: sidebar.activeTab === 'gene',
    isMappingTabActive: sidebar.activeTab === 'mapping',
    
    hasActiveTab: sidebar.activeTab !== null,
    
    getSidebarConfig: () => ({
      isOpen: sidebar.isOpen,
      activeTab: sidebar.activeTab,
      width: sidebar.width,
      position: sidebar.position,
    }),
    
    shouldShowContent: (tab: 'info' | 'gene' | 'mapping') => 
      sidebar.isOpen && sidebar.activeTab === tab,
  };
};